import { useEffect, useRef } from "react";

export type DailyPollingOptions = {
    intervalMs: number;
    enabled: boolean;
    onTick: () => Promise<void> | void;
};

const isPaused = (): boolean => document.hidden === true || navigator.onLine === false;

/**
 * FR-009: the next tick is scheduled only once the previous one has settled, so a
 * slow source can never accumulate overlapping requests the way `setInterval`
 * would. A hidden tab or an offline browser stops polling entirely and revalidates
 * once on return, instead of firing a backlog of missed ticks.
 */
export const useDailyPolling = ({ intervalMs, enabled, onTick }: DailyPollingOptions): void => {
    const onTickRef = useRef(onTick);
    onTickRef.current = onTick;

    useEffect(() => {
        if (!enabled || intervalMs <= 0) return undefined;

        let timer: ReturnType<typeof setTimeout> | null = null;
        let cancelled = false;

        const clear = (): void => {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
        };

        const schedule = (): void => { timer = setTimeout(() => { void run(); }, intervalMs); };

        const run = async (): Promise<void> => {
            timer = null;
            if (cancelled || isPaused()) return;
            await onTickRef.current();
            if (cancelled || isPaused()) return;
            schedule();
        };

        const resume = (): void => {
            clear();
            if (cancelled || isPaused()) return;
            void run();
        };

        if (!isPaused()) schedule();

        document.addEventListener("visibilitychange", resume);
        window.addEventListener("online", resume);
        window.addEventListener("offline", resume);

        return () => {
            cancelled = true;
            clear();
            document.removeEventListener("visibilitychange", resume);
            window.removeEventListener("online", resume);
            window.removeEventListener("offline", resume);
        };
    }, [enabled, intervalMs]);
};
